/**
 * Compact inline line scroller — sits next to the team/side label in the
 * team cell of a SpreadsTotals row. Shows up to 3 chips at a time (prev,
 * selected, next) with chevron buttons; selected chip is highlighted.
 *
 * Chip order is the GameEventRow's data sort:
 *   - Spreads: signed line ascending (-X away-favored on the left,
 *     +X home-favored on the right)
 *   - Totals: line ascending
 *
 * The default-selected index (closest YES to 50¢) is computed upstream;
 * this component just renders whatever index is current.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/infra/utils';

export interface LineChipItem {
  key: string;
  label: string;            // formatted line value, e.g. "-3.5", "9.5"
  priceCents: number | null;
}

interface MiniLineStripProps {
  chips: LineChipItem[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
  /** #648 — picking a line is a deliberate act on THIS card, so it may take a
   *  free hotkey focus. It must never steal focus from another card and never
   *  clear one: the chips stop propagation, so they never reach the row's
   *  focus-region handler, and the store call they use refuses both. This is
   *  the only way to focus a card without also expanding it. */
  onFocusCard?: () => void;
}

const VISIBLE = 3;

export function MiniLineStrip({
  chips,
  selectedIdx,
  onSelect,
  onFocusCard,
}: MiniLineStripProps) {
  if (chips.length === 0) return null;

  const select = (idx: number) => {
    onSelect(idx);
    onFocusCard?.();
  };

  // Window of `VISIBLE` chips centred on the selected index, clamped to
  // [0, chips.length).
  const half = Math.floor(VISIBLE / 2);
  let start = Math.max(0, selectedIdx - half);
  let end = Math.min(chips.length, start + VISIBLE);
  // Re-extend backwards if we hit the right edge so we still show VISIBLE.
  start = Math.max(0, end - VISIBLE);

  const canPrev = selectedIdx > 0;
  const canNext = selectedIdx < chips.length - 1;

  return (
    <div className="inline-flex items-center gap-0.5 font-mono text-[11px]">
      <button
        type="button"
        disabled={!canPrev}
        onClick={(e) => {
          e.stopPropagation();
          if (canPrev) select(selectedIdx - 1);
        }}
        className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-30"
        aria-label="Previous line"
      >
        <ChevronLeft size={12} />
      </button>
      {chips.slice(start, end).map((chip, i) => {
        const idx = start + i;
        const selected = idx === selectedIdx;
        return (
          <button
            key={chip.key}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              select(idx);
            }}
            className={cn(
              'rounded px-1.5 py-0.5 transition-colors',
              selected
                ? 'text-foreground bg-primary/15 border border-primary/40'
                : 'text-muted-foreground hover:text-foreground border border-transparent',
            )}
            title={`Line ${chip.label}${chip.priceCents != null ? ` — ${chip.priceCents}¢ YES` : ''}\nticker: ${chip.key}`}
          >
            {chip.label}
          </button>
        );
      })}
      <button
        type="button"
        disabled={!canNext}
        onClick={(e) => {
          e.stopPropagation();
          if (canNext) select(selectedIdx + 1);
        }}
        className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-30"
        aria-label="Next line"
      >
        <ChevronRight size={12} />
      </button>
    </div>
  );
}
